import React from 'react';
import { thirdPartyApi } from '@/api';
import { getFoxitStoreDomain, getQueryVariable, windowActionFn } from '@/utils/utils';
import intl from 'react-intl-universal';
import Base64 from 'crypto-js/enc-base64';
import sha1 from 'crypto-js/sha1';
import Utf8 from 'crypto-js/enc-utf8';
import Hex from 'crypto-js/enc-hex';
import { store } from '@/store';
import { navToWithBlank } from '@/components/header/utils/utils';
import { TO_ADMIN_CONSOLE } from '@/components/viewer/ui/constants/user.more.constants';
import { START_SHARE_DOC } from 'Components/viewer/ui/constants/collaboration.constants';

type AddInType = 'GoogleDrive' | 'Sharepoint' | 'OfficeAddIn' | 'Teams' | boolean
interface CheckPermissionObject {
  addInType: AddInType;
  action?: string;
  hasPermissionCallback?: Function;
  noPermissionCallback?: Function;
}
interface ReflectByPermissionObject { 
  hasPermission: boolean; 
  hasPermissionCallback: Function;
  addInType: AddInType; 
  isShowReflectMessage: boolean; 
}

export default class ThirdPartyService {
  constructor() {}
  getParams(): { thirdPartyLogin: string; email: string; userId: string; sign: string; timeStamp: string | number } {
    const thirdPartyLogin = getQueryVariable('thirdPartyLogin');
    const email = getQueryVariable('email');
    const userId = getQueryVariable('userid');
    const sign = getQueryVariable('sign');
    const timeStamp = getQueryVariable('timestamp') || Date.now();

    return {
      thirdPartyLogin,
      email,
      userId,
      sign,
      timeStamp,
    };
  }
  isLoginFlow() {
    const { thirdPartyLogin, email, userId, sign, timeStamp } = this.getParams();

    return !!(thirdPartyLogin && email && userId && sign && timeStamp);
  }
  getConstStr() {
    let constStr: string = '';
    const type: string = getQueryVariable('thirdPartyLogin');

    switch (type) {
      case 'WeCom':
        constStr = 'FxChannelWeCom';
        break;
      default:
        break;
    }

    return constStr;
  }
  getTestUrl() {
    const timeStamp = Date.now();
    const email = getQueryVariable('email');
    const userId = getQueryVariable('userid');
    const emailWordArray = Utf8.parse(email);
    const userIdWordArray = Utf8.parse(userId);
    const emailBase64 = Base64.stringify(emailWordArray);
    const userIdBase64 = Base64.stringify(userIdWordArray);
    const constStr = this.getConstStr();
    const sign = sha1(`${constStr}${email}${userId}${timeStamp}`).toString(Hex);

    return `${window.origin}?thirdPartyLogin=WeCom&email=${emailBase64}&userid=${userIdBase64}&sign=${sign}&timestamp=${timeStamp}`;
  }
  async getAccessToken() {
    const { thirdPartyLogin, email, userId, sign, timeStamp } = this.getParams();
    const decodeEmail = Base64.parse(email).toString(Utf8);
    const decodeUserId = Base64.parse(userId).toString(Utf8);
    const params = {
      type: thirdPartyLogin,
      email: decodeEmail,
      userId: decodeUserId,
      timeStamp,
      sign,
    };
    const res: any = await thirdPartyApi.getAccessToken(params);

    if (res && res.ret === 0 && res.data && res.data.token) {
      return {
        ret: 0,
        token: res.data.token,
      };
    }

    return {
      ret: res.ret,
      token: null,
    };
  }

  /***
   * Reflects the action based on the permission and displays corresponding messages.
   * @param {Object} options - The options object.
   * @param {boolean} options.hasPermission - Indicates whether the user has permission.
   * @param {Function} options.hasPermissionCallback - The callback function when the user has permission.
   * @param {string} options.addInType - The type of add-in.
   * @param {boolean} options.isShowReflectMessage - Indicates whether to show the reflect message.
   * @returns {boolean} - Indicates whether the action is reflected.
   ***/
  reflectByPermission({hasPermission, hasPermissionCallback, addInType, isShowReflectMessage}: ReflectByPermissionObject): boolean {
    const { isNewSubscribeStrategy, isNoFirstTrail, isBundle } = store.accountStore
    const { isEnterpriseUser } = store.userStore
    
    
    // ======= 个人/企业无权限用户开始监听订阅状态的变化 (为了用户订阅后能自动更新Cloud的订阅状态) =========
    if (!hasPermission) {
      store.appStore.showLoading(false);
      store.accountStore.subscribeSuccessAutoRefresh();
    }

    // ============================ 权限判断后显示的提示框 =============================
    const showSubscribeBundleMessage = this.showSubscribeBundleMessage.bind(this, {addInType, isShowReflectMessage})
    const showStartBundleTrialMessage = this.showStartBundleTrialMessage.bind(this, {addInType, isShowReflectMessage})
    const showContactSuperAdmin = this.showContactSuperAdmin.bind(this, {addInType, isShowReflectMessage})

    // ============================ 个人用户 =============================
    const showOrganizationOnlyMessage = this.showOrganizationOnlyMessage.bind(this, {addInType, isShowReflectMessage})
    // 正常订阅或试用, 且在2023新订阅计划 前 就已经开启的个人用户
    if (hasPermission && !isEnterpriseUser && !isNewSubscribeStrategy) {
      // GoogleDrive AddIn
      // Teams
      // Sharepoint
      hasPermissionCallback && hasPermissionCallback()

      return true
    }

    // 正常订阅或试用, 且在2023新订阅计划 后 才开启的个人用户
    if (hasPermission && !isEnterpriseUser && isNewSubscribeStrategy) {
      // GoogleDrive AddIn
      // Teams
      const { currentClickBtnName } = store.appStore

      if (addInType === 'Sharepoint' || (addInType === 'Teams' && currentClickBtnName === START_SHARE_DOC)) {
        // 提示需要企业用户才能使用
        showOrganizationOnlyMessage()

        return false
      } else {
        hasPermissionCallback && hasPermissionCallback()
      }

      return true
    }
    
    // 没有权限且已经试用过的个人用户
    if (!hasPermission && !isNoFirstTrail && !isEnterpriseUser) {
      if (addInType === 'Sharepoint' || addInType === 'Teams') {
        // 提示需要企业用户才能使用
        showOrganizationOnlyMessage()
      } else if (isBundle) {
        // 提示去订阅bundle
        showSubscribeBundleMessage()
      } else {
        // 提示去订阅cloud
        window.pdfui.customs.isAccountExpired(true)
      }

      return false
    }

    // 没有权限且从未试用过的个人用户
    if (!hasPermission && isNoFirstTrail && !isEnterpriseUser) {
      if (addInType === 'Sharepoint' || addInType === 'Teams') {
        // 提示需要企业用户才能使用
        showOrganizationOnlyMessage()
      } else {
        // 提示去开启bundle的试用
        showStartBundleTrialMessage()
      }

      return false
    }

    // ============================ 企业用户 =============================
    // 正常订阅或试用的企业用户
    if (hasPermission && isEnterpriseUser) {
      // GoogleDrive AddIn
      // Teams
      // Sharepoint
      hasPermissionCallback && hasPermissionCallback()

      return true
    }

    // 没有权限且已经试用过的企业用户
    if (!hasPermission && !isNoFirstTrail && isEnterpriseUser) {
      const { isSuperAdmin } = store.userStore

      // 超级管理员提示去订阅bundle（企业用户没有Cloud订阅）
      if (isSuperAdmin) {
        showSubscribeBundleMessage()
      }

      // 非超管提示去联系超管
      if (!isSuperAdmin) {
        showContactSuperAdmin()
      }

      return false
    }

    // 没有权限且从未试用过的企业用户
    if (!hasPermission && isNoFirstTrail && isEnterpriseUser) {
      // 提示去开启bundle的试用
      showStartBundleTrialMessage()

      return false
    }
  }

  /**
   *
   *
   * @param {AddInType} addInType
   * @param {Function} [hasPermissionCallback]
   * @param {Function} [noPermissionCallback]
   * @param {Boolean} [isShowReflectMessage]
   * @memberof ThirdPartyService
   */
  async checkPermissionAndReflect({addInType, hasPermissionCallback}: CheckPermissionObject, isShowReflectMessage: boolean = true ) {
    const { hasSignIn } = store.userStore

    // ============================ 未登录用户 =============================
    if (!hasSignIn) {
      store.appStore.showLoading(false);
      
      return false
    }

    const hasPermission = await store.userStore.hasSubscribePermission()

    return this.reflectByPermission({ hasPermission, hasPermissionCallback, addInType, isShowReflectMessage })
  }
  checkPermissionAndReflectSync({addInType, hasPermissionCallback}: CheckPermissionObject, isShowReflectMessage: boolean = true ) {
    const { hasSignIn } = store.userStore

    // ============================ 未登录用户 =============================
    if (!hasSignIn) {
      store.appStore.showLoading(false);
      
      return false
    }

    const hasPermission = store.userStore.hasSubscribePermissionSync()

    return this.reflectByPermission({ hasPermission, hasPermissionCallback, addInType, isShowReflectMessage })
  }
  showOrganizationOnlyMessage = ({isShowReflectMessage}) => {
    if (!isShowReflectMessage) {
      return;
    }

    const newSubscribeIntlSuffix = 'subscription.2023SubscribeStrategy';

    window.onShowWarningMessage(
      () => {},
      {
        containerClass: 'start-trail-dialog-from-teams',
        ok: `${newSubscribeIntlSuffix}.Personal.OrganizationOnly.OkBtn`,
        noCancel: true,
        contentComponent: (
          <div>
            {intl.getHTML(`${newSubscribeIntlSuffix}.Personal.OrganizationOnly.Alert`)}
            <span
              className="link-blue ml-5"
              onClick={() => {
                windowActionFn(window, 'open', ['https://www.foxit.com/shopping/']);
              }}>
              {intl.get(`${newSubscribeIntlSuffix}.Personal.OrganizationOnly.ContactUs`)}
            </span>
          </div>
        ),
      },
    );
  }
  showContactSuperAdmin = ({isShowReflectMessage}) => {
    if (!isShowReflectMessage) {
      return;
    }

    const newSubscribeIntlSuffix = 'subscription.2023SubscribeStrategy';
    const enterpriseBundleIntlSuffix = `${newSubscribeIntlSuffix}.Enterprise.Bundle`;

    window.onShowWarningMessage(() => {}, {
      containerClass: 'start-trail-dialog-from-teams',
      ok: `${enterpriseBundleIntlSuffix}.OkBtn`,
      noCancel: true,
      message: `${enterpriseBundleIntlSuffix}.ContactAdmin`
    });

    return;
  }
  showSubscribeBundleMessage = ({isShowReflectMessage}) => {
    const newSubscribeIntlSuffix = 'subscription.2023SubscribeStrategy';
    const personalBundleIntlSuffix = `${newSubscribeIntlSuffix}.Personal.Bundle`;
    const enterpriseBundleIntlSuffix = `${newSubscribeIntlSuffix}.Enterprise.Bundle`;
    const { isEnterpriseUser } = store.userStore;
    const { isTrialExpired } = store.accountStore

    if (!isShowReflectMessage) {
      return;
    }

    if (!isEnterpriseUser) {
      window.onShowWarningMessage(
        (isOk: Boolean) => {
          if (isOk) {
            store.accountStore.doGoToAppShopping({ target: 'app-detail', app_code: 'foxit_pdf_editor_ps_pro_individuals', planType: 'yearly' });
          }
        },
        {
          ok: `${personalBundleIntlSuffix}.UpgradeBtn`,
          message: `${personalBundleIntlSuffix}.Expired`,
        },
      );
    } else {
      if(isTrialExpired) {
        window.onShowWarningMessage((isOk: any) => {
          isOk && navToWithBlank(store.appStore.pheeAdminUrl, TO_ADMIN_CONSOLE);
        }, { message: 'account.superAdminNotActiveForSelf', title: 'PhantomPDF.Online.TitleWithOnline', ok: 'account.goAdminConsole', cancel: 'account.ok' }, false);
  
        return;
      } else {
        window.onShowWarningMessage(() => {}, {
          containerClass: 'subscribe-expired-dialog-from-teams',
          ok: `${enterpriseBundleIntlSuffix}.OkBtn`,
          noCancel: true,
          contentComponent: (
            <div>
              {intl.getHTML(`${enterpriseBundleIntlSuffix}.ExpiredForTeams`)}
              <span
                className="link-blue"
                onClick={() => {
                  store.accountStore.doGoToAppShopping({ target: 'app-detail', app_code: 'foxit_pdf_editor_ps_pro_business', planType: 'yearly' });
                }}>
                {intl.get(`${enterpriseBundleIntlSuffix}.AddToCardForTeams`)}
              </span>
            </div>
          ),
        });

        return;
      }
    }
  }

  /**
   * Shows the start bundle trial message.
   * @param {Object} options - The options object.
   * @param {boolean} options.isShowReflectMessage - Indicates whether to show the reflect message.
   */
  showStartBundleTrialMessage = ({isShowReflectMessage}) => {
      const ticketUrl = 'https://www.foxitsoftware.com'
      const newSubscribeIntlSuffix = 'subscription.2023SubscribeStrategy';
      const personalBundleIntlSuffix = `${newSubscribeIntlSuffix}.Personal.Bundle`;
      const enterpriseBundleIntlSuffix = `${newSubscribeIntlSuffix}.Enterprise.Bundle`;
      const { isEnterpriseUser } = store.userStore;

      if (!isShowReflectMessage) {
        return;
      }

      const appStoreUrl = getFoxitStoreDomain()

      if (!isEnterpriseUser) {
        window.onShowWarningMessage(
          (isOk: Boolean) => {
            if (isOk) {
              store.userStore.doGetTicket(ticketUrl, (ticket: string) => {
                windowActionFn(window, 'open', [`${appStoreUrl}/downloads/?ticket=${ticket}/#Foxit-PDF-Editor-Suite-Pro-Indviduals/`]);
              })
            }
          },
          {
            ok: `${personalBundleIntlSuffix}.FreeTrialBtn`,
            message: `${personalBundleIntlSuffix}.FreeTrial`,
          },
        );
      } else {
        const { isAdmin } = store.userStore

        if (isAdmin) {
          window.onShowWarningMessage(() => {}, {
            containerClass: 'start-trail-dialog-from-teams',
            ok: `${enterpriseBundleIntlSuffix}.OkBtn`,
            noCancel: true,
            contentComponent: (
              <div>
                {intl.getHTML(`${enterpriseBundleIntlSuffix}.FreeTrialForTeams`)}
                <span
                  className="link-blue"
                  onClick={() => {
                    store.userStore.doGetTicket(ticketUrl, (ticket: string) => {
                      windowActionFn(window, 'open', [`${appStoreUrl}/downloads/?ticket=${ticket}/#Foxit-PDF-Editor-Suite-Pro-Teams/`]);
                    })
                  }}>
                  {intl.get(`${enterpriseBundleIntlSuffix}.StartFreeTrialForTeams`)}
                </span>
              </div>
            ),
          });

          return;
        }

        if (!isAdmin) {
          window.onShowWarningMessage(() => {}, {
            containerClass: 'start-trail-dialog-from-teams',
            ok: `${enterpriseBundleIntlSuffix}.OkBtn`,
            noCancel: true,
            message: `${enterpriseBundleIntlSuffix}.ContactAdmin`
          });
  
          return;
        }
      }
  }

  /**
   * Returns the name of the third-party platform if the user is coming from a third-party platform, otherwise returns false.
   * @returns {string|boolean} The name of the third-party platform or false.
   */
  isFromThirdParty = () => {
    const fileParams = getQueryVariable('state'); // Google Drive Add-In
    const oneDriveState = getQueryVariable('oneDriveState');  // Sharepoint & Onedrive
    const officeAddIn = getQueryVariable('from')
    const teamsUserEmail = getQueryVariable('source');  // Teams

    if (fileParams || oneDriveState || teamsUserEmail) {
      let thirdPartyName: 'GoogleDrive' | 'Sharepoint' | 'OfficeAddIn' | 'Teams' | boolean

      if (fileParams) {
        thirdPartyName = 'GoogleDrive'
      }
      if (oneDriveState) {
        thirdPartyName = 'Sharepoint'
      }
      if (officeAddIn) {
        thirdPartyName = 'OfficeAddIn'
      }
      if (teamsUserEmail) {
        thirdPartyName = 'Teams'
      }

      return thirdPartyName
    }

    return false
  }
}
